import { showAddOrEditFineRuleState } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import StyledDateInput from "@/components/common/DateInput.styled";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useGetAllCouncils } from "@/services/councils";
import { useGetServicesByCouncil } from "@/services/services";
import { useCreateFineRule, useUpdateFineRule } from "@/services/enhanced-fines";
import { Button, NumberInput, Select, Switch, Card, Stack, Group, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { ModalLayout } from "moh-ui";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function AddUpdateFineRule() {
    const [show, setShow] = useRecoilState(showAddOrEditFineRuleState);
    const { user } = useAuth();
  const council = useUserCouncil();
    const [data, setData] = useState({
        name: '',
        description: '',
        productCode: '',
        amount: 0,
        councilId: null as string | null,
        serviceId: null as string | null,
        isActive: true,
        effectiveDate: new Date(),
        rules: {
            daysAfterDeadline: 1,
            calculateDailyIncrease: false,
            dailyIncreaseAmount: 0,
            maxAmount: 0,
        }
    });

    const {
        mutate: createFineRule,
        isPending: creatingFineRule
    } = useCreateFineRule();

    const {
        mutate: updateFineRule,
        isPending: updatingFineRule
    } = useUpdateFineRule();

    const queryClient = useQueryClient();

    const {
        data: councils,
    } = useGetAllCouncils();

    const {
        data: services,
    } = useGetServicesByCouncil({
        councilId: data.councilId || undefined,
        enabled: !!data.councilId,
    });

    useEffect(() => {
        if (show?.action === 'edit' && show.fineRule) {
            setData({
                name: show.fineRule.name,
                description: show.fineRule.description,
                productCode: show.fineRule.productCode,
                amount: show.fineRule.amount,
                councilId: show.fineRule.councilId,
                serviceId: show.fineRule.serviceId || null,
                isActive: show.fineRule.isActive,
                effectiveDate: new Date(show.fineRule.effectiveDate),
                rules: {
                    daysAfterDeadline: show.fineRule.rules?.daysAfterDeadline || 1,
                    calculateDailyIncrease: show.fineRule.rules?.calculateDailyIncrease || false,
                    dailyIncreaseAmount: show.fineRule.rules?.dailyIncreaseAmount || 0,
                    maxAmount: show.fineRule.rules?.maxAmount || 0,
                }
            });
        } else {
            setData({
                name: '',
                description: '',
                productCode: '',
                amount: 0,
                councilId: user?.roles?.[0]?.roleName === "ADMINCOUNCIL" ? (council?.id ?? null) : null,
                serviceId: null,
                isActive: true,
                effectiveDate: new Date(),
                rules: {
                    daysAfterDeadline: 1,
                    calculateDailyIncrease: false,
                    dailyIncreaseAmount: 0,
                    maxAmount: 0,
                }
            });
        }
    }, [show, user]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!data.councilId) {
            notifications.show({
                title: 'Error',
                message: 'Please select a council',
                color: 'red'
            });
            return;
        }

        if (!data.serviceId) {
            notifications.show({
                title: 'Error',
                message: 'Please select a service',
                color: 'red'
            });
            return;
        }

        const params = {
            ...data,
            councilId: data.councilId!,
            serviceId: data.serviceId!,
            effectiveDate: data.effectiveDate.toISOString(),
        };

        if (show?.action === 'add') {
            createFineRule(params, {
                onSuccess: () => {
                    if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["fine-rules", council?.id],
                        });
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["fine-rules"],
                        });
                    }
                    setShow(null);
                }
            });
        } else if (show?.action === 'edit' && show.fineRule) {
            updateFineRule({
                id: show.fineRule.id,
                ...params,
            }, {
                onSuccess: () => {
                    if (user?.roles?.[0]?.roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["fine-rules", council?.id],
                        });
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["fine-rules"],
                        });
                    }
                    setShow(null);
                }
            });
        }
    };

    return (
        <ModalLayout
            open={Boolean(show?.show)}
            onClose={() => setShow(null)}
            size="lg"
        >
            <div className="mb-5">
                <h2 className="text-lg font-semibold">
                    {show?.action === 'add' ? 'Add Fine Rule' : 'Update Fine Rule'}
                </h2>
                <p className="text-sm text-gray-500">
                    {show?.action === 'add' ? 'Create a new fine rule with calculation settings.' : 'Update the fine rule details and calculation settings.'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Basic Information */}
                <Card withBorder p="md">
                    <Stack gap="md">
                        <Text fw={500} size="sm">Basic Information</Text>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CustomInput 
                                label="Fine Name" 
                                alwaysFocused
                                type="text"
                                value={data.name}
                                onChange={(e) => setData({ ...data, name: e.target.value })}
                                placeholder="e.g., Late Submission Fine"
                                required
                            />

                            <CustomInput
                                label="Product Code"
                                alwaysFocused
                                type="text"
                                value={data.productCode}
                                onChange={(e) => setData({ ...data, productCode: e.target.value.toUpperCase() })}
                                placeholder="e.g., LATE_SUBMISSION"
                                required
                            />
                        </div>

                        <CustomInput 
                            label="Description" 
                            alwaysFocused
                            textarea
                            value={data.description}
                            onChange={(e) => setData({ ...data, description: e.target.value })}
                            placeholder="Describe when this fine applies"
                            required
                            rows={3}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {user?.roles?.[0]?.roleName === "ADMIN" && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Select Council</label>
                                    <Select
                                        value={data.councilId ?? ""}
                                        onChange={(val) => setData({ ...data, councilId: val, serviceId: null })}
                                        data={councils?.data.map((item) => ({
                                            value: item.id,
                                            label: item.councilName + " (" + item.councilCode + ")",
                                        })) || []}
                                        placeholder="Select Council"
                                        required
                                        className="w-full"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium mb-1">Select Service</label>
                                <Select
                                    value={data.serviceId ?? ""}
                                    onChange={(val) => setData({ ...data, serviceId: val })}
                                    data={services?.data?.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                    })) || []}
                                    placeholder="Select Service"
                                    disabled={!data.councilId}
                                    required
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Base Amount (RWF)</label>
                                <NumberInput
                                    value={data.amount}
                                    onChange={(val) => setData({ ...data, amount: Number(val) || 0 })}
                                    min={0}
                                    step={1000}
                                    required
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Effective Date</label>
                                <StyledDateInput
                                    required
                                    value={data.effectiveDate}
                                    placeholder="Select effective date"
                                    onChange={(value) => setData({ ...data, effectiveDate: value || new Date() })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <StyledDateInput
                                required
                                value={data.effectiveDate}
                                label="Effective Date"
                                placeholder="Select effective date"
                                onChange={(value) => setData({ ...data, effectiveDate: value || new Date() })}
                            />

                            <div className="flex items-center gap-2 pt-6">
                                <Switch
                                    checked={data.isActive}
                                    onChange={(event) => setData({ ...data, isActive: event.currentTarget.checked })}
                                    label="Active"
                                />
                            </div>
                        </div>
                    </Stack>
                </Card>

                {/* Calculation Rules */}
                <Card withBorder p="md">
                    <Stack gap="md">
                        <Text fw={500} size="sm">Calculation Rules</Text>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Days After Deadline</label>
                                <NumberInput
                                    value={data.rules.daysAfterDeadline}
                                    onChange={(val) => setData({
                                        ...data,
                                        rules: { ...data.rules, daysAfterDeadline: Number(val) || 1 }
                                    })}
                                    min={1}
                                    required
                                    className="w-full"
                                    description="Apply fine after this many days past deadline"
                                />
                            </div>

                            <div className="flex items-center gap-2 pt-6">
                                <Switch
                                    checked={data.rules.calculateDailyIncrease}
                                    onChange={(event) => setData({
                                        ...data,
                                        rules: { ...data.rules, calculateDailyIncrease: event.currentTarget.checked }
                                    })}
                                    label="Calculate Daily Increase"
                                />
                            </div>
                        </div>

                        {data.rules.calculateDailyIncrease && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Daily Increase Amount (RWF)</label>
                                    <NumberInput
                                        value={data.rules.dailyIncreaseAmount}
                                        onChange={(val) => setData({
                                            ...data,
                                            rules: { ...data.rules, dailyIncreaseAmount: Number(val) || 0 }
                                        })}
                                        min={0}
                                        step={500}
                                        className="w-full"
                                        description="Amount to add each day"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Maximum Amount (RWF)</label>
                                    <NumberInput
                                        value={data.rules.maxAmount}
                                        onChange={(val) => setData({
                                            ...data,
                                            rules: { ...data.rules, maxAmount: Number(val) || 0 }
                                        })}
                                        min={0}
                                        step={5000}
                                        className="w-full"
                                        description="Maximum fine amount (0 = no limit)"
                                    />
                                </div>
                            </div>
                        )}
                    </Stack>
                </Card>

                <div className="flex justify-end items-center gap-4 pt-4 border-t">
                    <Button
                        variant="outline"
                        color="gray"
                        onClick={() => setShow(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={creatingFineRule || updatingFineRule}
                    >
                        {show?.action === 'add' ? 'Create Fine Rule' : 'Update Fine Rule'}
                    </Button>
                </div>
            </form>
        </ModalLayout>
    );
}
